import { ChannelDefinition, IConnectionStatus } from "../transport/types";
import SkybusProviderBase from "../provider/base";
import { PublishOptions, WireMsg, WireType } from "../types";
import { debugLog } from "./debugConsoleLog";
import {
  HistoryListenerCallback,
  LatestListener,
  LatestListenerCallback,
  Subscription,
} from "./subscription";

export const isSubscriptionOfType = <D extends WireMsg>(
  sub: Subscription<WireMsg>,
  type: WireType<D>
): // @ts-ignore
sub is Subscription<D> => {
  return sub.type === type;
};

interface ISubscriptionInfo {
  type: WireType<WireMsg>;
  subscription: Subscription<WireMsg>;
  onLatest?: LatestListenerCallback<WireMsg>;
  onHistory?: HistoryListenerCallback<WireMsg>;
}

/**
 * Async operation (promise) for a response on some given channel that also has a stop function.
 * Calling stop will NOT undo the original request, but it will stop any retries and listeners.
 */
class SkybusPromise<
  RequestChannelType extends WireMsg,
  ResponseChannelType extends WireMsg,
> extends Promise<ResponseChannelType> {
  private requestHash: string;

  constructor(
    ...args: [
      ...ConstructorParameters<typeof Promise<ResponseChannelType>>,
      ChannelDefinition<WireType<RequestChannelType>>,
      ChannelDefinition<WireType<ResponseChannelType>>,
    ]
  ) {
    const [executor, requestChannel, responseChannel] = args;
    super(executor);
    requestChannel &&
      responseChannel &&
      (this.requestHash = getRequestId(requestChannel, responseChannel));
  }

  public stop() {
    stopRetriesForRequest(this.requestHash);
  }
}

// 2 seconds
const SUBSCRIPTION_RETRY_DEFAULT_INTERVAL = 1000 * 2;
/**
 * A pending request is defined by the combination of the request channel and response channel.
 * For each such combination we only want to maintain 1 retry interval and response listener. This is because skybus
 * only allows a single subscription per channel. So if a new request comes in
 * on the same key we will drop the existing retry interval as it can no longer be completed.
 */
const PENDING_REQUEST_MAP: Map<
  string,
  { retryInterval: NodeJS.Timeout; latestListener: LatestListener<any> }
> = new Map();

function stopRetriesForRequest(requestHash: string): void {
  const pendingRequest = PENDING_REQUEST_MAP.get(requestHash);
  if (!pendingRequest) return;
  clearInterval(pendingRequest.retryInterval);
  pendingRequest.latestListener.stop();
  PENDING_REQUEST_MAP.delete(requestHash);
}

function getRequestId<ResponseChannelType extends WireMsg, RequestChannelType extends WireMsg>(
  requestChannel: ChannelDefinition<WireType<RequestChannelType>>,
  responseChannel: ChannelDefinition<WireType<ResponseChannelType>>
): string {
  return `${requestChannel.channel}, ${responseChannel.channel}`;
}

export default class UnifiedSkybus implements IConnectionStatus {
  private subscriptions: Map<string, ISubscriptionInfo>;

  constructor(readonly provider: SkybusProviderBase) {
    this.subscriptions = new Map();
    this.provider = provider;

    this.subscribe = this.subscribe.bind(this);
  }

  /**
   * Subscribe to a channel based on ChannelDefinition
   * @param channel ChannelDefinition imported from @skydio/channels
   */
  public subscribe<D extends WireMsg>(channel: ChannelDefinition<WireType<D>>): Subscription<D>;

  /**
   * Subscribe based on channel and type
   *
   * WARNING:
   * This method doesn't have the safety of a ChannelDefinition subscribe.
   * You can provide the wrong type, and TS won't notice (the browser will).
   *
   * @param channel channel name string
   * @param type channel decoder, LcmType or .deserializeBinary pbtype method
   */
  public subscribe<D extends WireMsg>(channel: string, type: WireType<D>): Subscription<D>;
  public subscribe<D extends WireMsg>(
    channel: string | Exclude<ChannelDefinition<WireType<D>>, string>,
    type?: WireType<D>
  ): Subscription<D> {
    if (typeof channel === "string") {
      if (!type) {
        throw new Error("Channel provided as string, but no type provided to use to decode!");
      }
      return this.getSubscription(channel, type);
    } else {
      return this.getSubscription(channel.channel, channel.type);
    }
  }

  private async genActiveSubscription<D extends WireMsg>(
    channel: ChannelDefinition<WireType<D>>,
    debugKey?: string
  ): Promise<Subscription<D>> {
    return this.getSubscription(channel.channel, channel.type).activate(debugKey);
  }

  /**
   * Make a single request over skybus using short lived subscriptions.
   *
   * @param requestChannel channel for publishing your request on, must have a setUtime method
   * @param responseChannel channel where you are expecting a response based on your request
   * @param request the request object
   * @param matchRequestToResponse function to determine if the latest response on responseChannel
   *  is the one that matches your input request.
   *
   *  IMPORTANT NOTE: the logic here will vary from
   *  endpoint to endpoint, and just because you send a request doesn't mean the next response on the
   *  target channel is the one answering your request.
   *
   * @param config.timeout time in ms, once exceeded we will retry up to config.retry times then reject. Default 10s.
   * @param config.retries how many times to retry sending the request if the timeout passes without an answer. Default 0.
   * @param config.debugKey set to the name of your request. Will print console messages for each step
   * of the request with this name included in them for filtering purposes. Don't commit code with this
   * set, it's only for figuring out if something is wrong with your requests while developing.
   *
   * @returns a promise that will resolve with the response matching your request using your provided
   * matching function. Will reject if timeout is exceeded
   */
  public makeRequest<
    ResponseChannelType extends WireMsg,
    RequestChannelType extends WireMsg & { setUtime(utime: number): void },
  >(
    requestChannel: ChannelDefinition<WireType<RequestChannelType>>,
    responseChannel: ChannelDefinition<WireType<ResponseChannelType>>,
    request: RequestChannelType,
    matchRequestToResponse: (latest: ResponseChannelType, requested: RequestChannelType) => boolean,
    config?: {
      timeout?: number;
      retries?: number;
      debugKey?: string;
    }
  ): SkybusPromise<RequestChannelType, ResponseChannelType> {
    const { debugKey } = config ?? {};
    const requestHash = getRequestId(requestChannel, responseChannel);
    // skybus only allows 1 callback per channel. If a request is already pending on these same
    // channels we need to cancel it as it will never complete
    stopRetriesForRequest(requestHash);
    return new SkybusPromise(
      async (resolve, reject) => {
        let activeResponseChannel;
        try {
          activeResponseChannel = await this.genActiveSubscription(responseChannel, debugKey);
        } catch (e) {
          reject(e);
          return;
        }
        debugLog({
          debugKey,
          requestSentOn: requestChannel.channel,
          listeningForResponseOn: responseChannel.channel,
          withConfig: config,
        });
        let retriesLeft = config?.retries ?? 0;
        const retryInterval = setInterval(() => {
          if (retriesLeft === 0) {
            debugLog({
              debugKey,
              ranOutOfRetries: true,
            });
            stopRetriesForRequest(requestHash);
            reject(
              `Request failed, retries exhausted. Request channel ${
                requestChannel.channel
              } response channel ${responseChannel.channel} request: ${JSON.stringify(request)}`
            );
          } else {
            retriesLeft--;
            debugLog({
              debugKey,
              retry: (config?.retries ?? 0) - retriesLeft,
              request,
            });
            // Update the request utime between retries, since this should correspond to the time when the request is sent
            request.setUtime(Date.now() * 1000); // utime is in microseconds
            this.publish(requestChannel, request);
          }
        }, config?.timeout ?? SUBSCRIPTION_RETRY_DEFAULT_INTERVAL);
        const latestListener = activeResponseChannel.listenLatest(response => {
          debugLog({
            debugKey,
            gotResponse: response,
            forRequest: request,
          });
          if (matchRequestToResponse(response, request)) {
            debugLog({
              debugKey,
              responseMatchedRequest: true,
            });
            stopRetriesForRequest(requestHash);
            resolve(response);
          }
        });
        PENDING_REQUEST_MAP.set(requestHash, {
          latestListener,
          retryInterval,
        });
        this.publish(requestChannel, request);
      },
      requestChannel,
      responseChannel
    );
  }

  public publish = <D extends WireMsg>(
    channel: string | ChannelDefinition<WireType<D>>,
    msg: D,
    options?: PublishOptions
  ) => {
    this.provider.publish(channel, msg, options);
  };

  public isConnected = (): boolean => {
    return this.provider.isConnected();
  };

  public addConnectionStatusListener = (callback: (connected: boolean) => void): (() => void) => {
    return this.provider.addConnectionStatusListener(callback);
  };

  /**
   * Returns current subscription or creates a new one if necessary
   * @param channel
   * @param type
   * @param debugKey
   * @returns
   */
  private getSubscription = <D extends WireMsg>(
    channel: string,
    type: WireType<D>,
    debugKey?: string
  ): Subscription<D> => {
    // check if subscription already exists
    debugLog({
      debugKey,
      creatingSubscriptionForChannel: channel,
    });
    if (this.subscriptions.has(channel)) {
      const info = this.subscriptions.get(channel)!;
      if (isSubscriptionOfType(info.subscription, type)) {
        debugLog({
          debugKey,
          foundExistingSubscriptionFor: channel,
        });
        return info.subscription;
      }
      throw new Error("Cached subscription was not of same type!");
    }

    // we need to create a new subscription
    const subscription = new Subscription<D>(this.provider, channel, type);

    // @ts-ignore
    this.subscriptions.set(channel, { type, subscription });

    return subscription;
  };
}
